<template>
  <div class="product-configuration">
    <div
      class="product-configuration-group-container"
      v-for="(configurationGroup, index) of configurations"
      :key="`configuration-${configurationGroup.attributeId}`"
    >
      <div>
        <span class="bold">{{ configurationGroup.name }}:</span> {{ selectedConfiguration(configurationGroup).value }}
      </div>
      <div class="product-configuration-group" v-if="configurationGroup.type === 'BUTTON'">
        <button
          class="product-configuration-button button tiny"
          v-for="configuration in configurationGroup.configuration"
          @click="loadConfiguration({ configurationGroup, value: configuration })"
          :key="configuration.valueId"
          :class="{ 'outlined coral': configuration.selected, 'text grey': !configuration.selected }"
          :aria-label="`${configurations[index].name} Option: ${configuration.value}`"
        >
          {{ configuration.value }}
        </button>
      </div>
      <div class="product-configuration-group" v-else-if="configurationGroup.type === 'CIRCLE'">
        <a
          v-for="configuration in configurationGroup.configuration"
          :key="configuration.valueId"
          class="product-configuration-circle"
          @click="loadConfiguration({ configurationGroup, value: configuration })"
          tabindex="0"
          :aria-label="`${configurationGroup.name} Option: ${configuration.value}`"
        >
          <div class="circle checkbox-circle" :class="{ 'checkbox-circle-selected': configuration.selected }">
            <div class="checkbox-circle-image">
              <ResponsiveImage
                :url="configuration.imageUrl + '?fit=max&w={width}&q={q}'"
                :widths="[32]"
                :breakpoints="[]"
                :alt="`Color: ${configuration.value}`"
              />
            </div>
          </div>
        </a>
      </div>
      <div class="product-configuration-group" v-else-if="configurationGroup.type === 'TILE'">
        <a
          v-for="configuration in configurationGroup.configuration"
          :key="configuration.valueId"
          class="product-configuration-tile"
          :class="{ 'product-configuration-tile-selected': configuration.selected }"
          @click="loadConfiguration({ configurationGroup, value: configuration })"
        >
          <img v-if="configuration.imageUrl && configuration.imageUrl.endsWith('.svg')" :src="configuration.imageUrl" :alt="configuration.value" />
          <ResponsiveImage v-else :image="{ url: configuration.imageUrl }" />
        </a>
      </div>
      <div v-else>
        {{ configurationGroup }}
      </div>
    </div>
  </div>
</template>

<script>
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';

export default {
  name: 'ProductConfigurator',
  components: {
    ResponsiveImage,
  },
  props: {
    configurations: {},
  },
  methods: {
    selectedConfiguration(configurationGroup) {
      if (configurationGroup && configurationGroup.configuration) {
        return configurationGroup.configuration.find((config) => config.selected) || {};
      }
      return {};
    },

    async loadConfiguration(config) {
      this.$emit('update:modelValue', config);
    },
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.product-configuration {
  display: block;
  margin-left: 0px;

  @media (min-width: $mobile-breakpoint) {
    display: flex;
    flex-wrap: wrap;
    margin-right: -1px;
    margin-left: calc(-1 * var(--space-4));
  }

  @media (min-width: $desktop-breakpoint) {
    display: block;
    margin-right: auto;
    margin-left: 0px;
  }
}

.product-configuration-divider {
  display: block;

  @media (min-width: $mobile-breakpoint) {
    display: none;
  }

  @media (min-width: $desktop-breakpoint) {
    display: block;
  }
}

.product-configuration-group-container {
  padding-bottom: 0;
  margin-bottom: var(--space-4);
  border-right: none;
  /* purgecss ignore */
  &:not(:last-child) {
    border-bottom: 1px solid var(--grey-ultra-light);
    padding-bottom: var(--space-4);
  }

  @media (min-width: $mobile-breakpoint) {
    flex-grow: 1;
    padding-bottom: 0;
    padding-left: var(--space-4);
    padding-right: var(--space-4);
    margin-bottom: var(--space-4);
    min-width: 180px;
    border-right: 1px solid var(--grey-ultra-light);
    /* purgecss ignore */
    &:not(:last-child) {
      border-bottom: none;
    }
  }

  @media (min-width: $desktop-breakpoint) {
    padding-bottom: 0;
    margin-bottom: var(--space-4);
    padding-left: 0;
    padding-right: 0;
    min-width: auto;
    border-right: none;

    /* purgecss ignore */
    &:not(:last-child) {
      border-bottom: 1px solid var(--grey-ultra-light);
      padding-bottom: var(--space-4);
    }
  }
}

.product-configuration-group {
  margin-top: var(--space-2);
  display: flex;
  flex-wrap: wrap;

  & > * {
    margin-right: var(--space-2);
  }

  .product-configuration-button {
    margin-bottom: var(--space-2);
  }
}

.checkbox-circle {
  @media (max-width: 382px) {
    margin-bottom: var(--space-1); 
  }

  &.checkbox-circle-selected,
  &:hover {
    box-shadow: 0 0 0 1px var(--coral);
  }
}

.checkbox-circle {
  @media (max-width: 355px) {
    height: 24px;
    width: 24px !important;
  }
  height: 30px;
  width: 30px;
}

.checkbox-circle :deep(img) {
  @media (max-width: 355px) {
    height: 18px;
    width: 18px !important;
  }
  height: 24px;
  width: 24px !important;
  border-radius: var(--circle-border-radius);
  background-color: var(--white);
  display: block;
  border: 3px solid var(--white);
  box-shadow: 0 0 0 1px var(--transparent);
  overflow: hidden;
  transition: box-shadow 150ms ease-in-out;
}

.product-configuration-tile {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 66px;
  height: 66px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0);
  border-radius: 8px;
  transition: box-shadow 150ms ease-in-out;

  /* purgecss ignore */
  &:hover,
  &.product-configuration-tile-selected {
    box-shadow: 0 0 0 1px var(--coral);
  }
}
</style>
